import { useEffect, useState } from "react";
import { Play, Pause, Search, Home, Library, ListMusic, Clock3, Sparkles, Flame, Disc3 } from "lucide-react";
import { api } from "../api";
import { usePlayer } from "../player";
import { useAuth } from "../auth";
import { mediaUrl, fmtNum, fmtDur } from "../config";
import { Spinner, Empty, Avatar } from "../components/cards";

const greeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 18) return "Good afternoon";
  return "Good evening";
};

const ArtworkCard = ({ item, tracks, testId }) => {
  const { playTrack, current, playing, toggle } = usePlayer();
  const isCurrent = current?.id === item.id;
  const img = mediaUrl(item.artwork || item.coverImage);
  return (
    <div
      className="group w-40 shrink-0 cursor-pointer rounded-2xl border border-white/5 bg-white/[0.03] p-3 transition-all duration-300 hover:-translate-y-1 hover:border-white/15 hover:bg-white/[0.07] sm:w-44"
      data-testid={testId}
      onClick={() => (tracks ? (isCurrent ? toggle() : playTrack(item, tracks)) : null)}
    >
      <div className="relative aspect-square overflow-hidden rounded-xl bg-surface">
        {img ? (
          <img src={img} alt={item.title || item.name} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-slate-700">
            {tracks ? <Disc3 size={34} /> : <ListMusic size={34} />}
          </div>
        )}
        {tracks && (
          <div className={`absolute bottom-2 right-2 flex h-10 w-10 items-center justify-center rounded-full bg-crimson shadow-xl transition-all duration-300 ${isCurrent ? "opacity-100" : "translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100"}`}>
            {isCurrent && playing ? <Pause size={16} className="text-white" /> : <Play size={16} className="ml-0.5 text-white" fill="white" />}
          </div>
        )}
      </div>
      <p className="mt-3 truncate font-heading text-sm font-bold text-white">{item.title || item.name}</p>
      <p className="truncate text-xs text-slate-500">{item.artist || item.owner?.displayName || item.description || ""}</p>
    </div>
  );
};

const TrackRow = ({ track, index, list }) => {
  const { playTrack, current, playing, toggle } = usePlayer();
  const isCurrent = current?.id === track.id;
  return (
    <button
      onClick={() => (isCurrent ? toggle() : playTrack(track, list))}
      data-testid={`track-${track.id}`}
      className={`group flex w-full items-center gap-4 rounded-xl px-3 py-2 text-left transition-colors ${isCurrent ? "bg-crimson/10" : "hover:bg-white/[0.05]"}`}
    >
      <span className={`w-5 text-center font-mono text-xs ${isCurrent ? "text-crimson" : "text-slate-600"}`}>
        {isCurrent && playing ? "♪" : index + 1}
      </span>
      <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-surface">
        {mediaUrl(track.artwork) && <img src={mediaUrl(track.artwork)} alt="" className="h-full w-full object-cover" />}
        <div className={`absolute inset-0 flex items-center justify-center bg-black/40 transition-opacity ${isCurrent ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}>
          {isCurrent && playing ? <Pause size={14} className="text-white" /> : <Play size={14} className="text-white" fill="white" />}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className={`truncate font-heading text-sm font-semibold ${isCurrent ? "text-crimson" : "text-white"}`}>{track.title}</p>
        <p className="truncate text-xs text-slate-500">{track.artist}{track.genre ? ` · ${track.genre}` : ""}</p>
      </div>
      <span className="hidden font-mono text-[10px] text-slate-600 sm:inline">{fmtNum(track.plays)} plays</span>
      <span className="font-mono text-[10px] text-slate-600">{track.durationSec > 0 ? fmtDur(track.durationSec) : ""}</span>
    </button>
  );
};

const Row = ({ icon: Icon, title, children, testId }) => (
  <section className="mb-10" data-testid={testId}>
    <h2 className="mb-4 flex items-center gap-2.5 font-heading text-lg font-bold tracking-tight text-white">
      {Icon && <Icon size={17} className="text-crimson" />}
      {title}
    </h2>
    <div className="flex gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {children}
    </div>
  </section>
);

const Music = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState("home");
  const [tracks, setTracks] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [q, setQ] = useState("");
  const [recentIds, setRecentIds] = useState([]);

  useEffect(() => {
    api.get("/music").then((r) => setTracks(Array.isArray(r) ? r : r.tracks || [])).catch(() => setTracks([]));
    api.get("/albums").then((r) => setAlbums(Array.isArray(r) ? r : r.albums || [])).catch(() => {});
    api.get("/albums/featured").then((r) => setFeatured(Array.isArray(r) ? r : r ? [r] : [])).catch(() => {});
    api.get("/playlists").then((r) => setPlaylists(Array.isArray(r) ? r : r.playlists || [])).catch(() => {});
    try {
      setRecentIds(JSON.parse(localStorage.getItem("mcs_recent") || "[]"));
    } catch {}
  }, []);

  if (tracks === null) return <Spinner />;

  const byId = Object.fromEntries(tracks.map((t) => [t.id, t]));
  const recent = recentIds.map((id) => byId[id]).filter(Boolean);
  const trending = [...tracks].sort((a, b) => (b.plays || 0) - (a.plays || 0)).slice(0, 10);
  const newReleases = [...tracks].sort((a, b) => new Date(b.releaseDate || 0) - new Date(a.releaseDate || 0)).slice(0, 10);
  const myUploads = user ? tracks.filter((t) => t.creatorId === user.id) : [];
  const featuredTiles = (featured.length ? featured : albums).slice(0, 6);
  const searchResults = q.trim()
    ? tracks.filter((t) => `${t.title} ${t.artist} ${t.genre || ""}`.toLowerCase().includes(q.trim().toLowerCase()))
    : [];

  const tabs = [
    ["home", "Home", Home],
    ["search", "Search", Search],
    ["library", "Library", Library],
    ["playlists", "Playlists", ListMusic],
  ];

  return (
    <div className="mx-auto max-w-6xl pb-32" data-testid="music-page">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-white">
            {greeting()}{user ? `, ${user.displayName || user.username}` : ""}
          </h1>
          <p className="mt-1 text-sm text-slate-500">Your music, right where you left it.</p>
        </div>
        <div className="flex gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1" data-testid="music-tabs">
          {tabs.map(([k, label, Icon]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`music-tab-${k}`}
              className={`flex items-center gap-1.5 rounded-full px-4 py-2 font-heading text-xs font-bold transition-colors ${tab === k ? "bg-crimson text-white" : "text-slate-400 hover:text-white"}`}
            >
              <Icon size={13} />
              <span className="hidden sm:inline">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {tab === "home" && (
        <>
          {featuredTiles.length > 0 && (
            <div className="mb-10 grid grid-cols-2 gap-3 lg:grid-cols-3" data-testid="music-featured-tiles">
              {featuredTiles.map((a) => (
                <div key={a.id} className="group flex items-center gap-3 overflow-hidden rounded-xl bg-white/[0.05] transition-colors hover:bg-white/[0.1]" data-testid={`featured-tile-${a.id}`}>
                  <div className="h-16 w-16 shrink-0 bg-surface sm:h-20 sm:w-20">
                    {mediaUrl(a.artwork || a.coverImage) && <img src={mediaUrl(a.artwork || a.coverImage)} alt="" className="h-full w-full object-cover" />}
                  </div>
                  <p className="truncate pr-3 font-heading text-sm font-bold text-white">{a.title || a.name}</p>
                </div>
              ))}
            </div>
          )}

          {recent.length > 0 && (
            <Row icon={Clock3} title="Recently played" testId="row-recent">
              {recent.map((t) => <ArtworkCard key={t.id} item={t} tracks={recent} testId={`recent-${t.id}`} />)}
            </Row>
          )}

          <Row icon={Flame} title="Trending now" testId="row-trending">
            {trending.map((t) => <ArtworkCard key={t.id} item={t} tracks={trending} testId={`trending-${t.id}`} />)}
          </Row>

          {playlists.length > 0 && (
            <Row icon={Sparkles} title="Made for you" testId="row-made-for-you">
              {playlists.map((p) => <ArtworkCard key={p.id} item={p} testId={`madeforyou-${p.id}`} />)}
            </Row>
          )}

          <Row icon={Disc3} title="New releases" testId="row-new-releases">
            {newReleases.map((t) => <ArtworkCard key={t.id} item={t} tracks={newReleases} testId={`new-${t.id}`} />)}
          </Row>

          {albums.length > 0 && (
            <Row icon={Library} title="Albums" testId="row-albums">
              {albums.map((a) => <ArtworkCard key={a.id} item={a} testId={`album-${a.id}`} />)}
            </Row>
          )}
        </>
      )}

      {tab === "search" && (
        <div data-testid="music-search-tab">
          <div className="mb-6 flex max-w-xl items-center gap-3 rounded-full border border-white/10 bg-white/[0.05] px-5 py-3">
            <Search size={16} className="shrink-0 text-slate-500" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search tracks, artists, genres…"
              className="w-full bg-transparent text-sm text-white outline-none placeholder-slate-600"
              data-testid="music-search-input"
            />
          </div>
          {q.trim() ? (
            searchResults.length ? (
              <div className="space-y-1">{searchResults.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={searchResults} />)}</div>
            ) : (
              <Empty>No tracks match "{q}".</Empty>
            )
          ) : (
            <Empty>Start typing to search the music library.</Empty>
          )}
        </div>
      )}

      {tab === "library" && (
        <div data-testid="music-library-tab">
          {!user ? (
            <Empty>Sign in to see your uploads and listening history.</Empty>
          ) : (
            <>
              <h2 className="mb-3 font-heading text-base font-bold text-white">Your uploads</h2>
              {myUploads.length ? (
                <div className="mb-8 space-y-1">{myUploads.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={myUploads} />)}</div>
              ) : (
                <p className="mb-8 text-sm text-slate-600">You haven't uploaded music yet — use Upload music on your profile.</p>
              )}
              <h2 className="mb-3 font-heading text-base font-bold text-white">Recently played</h2>
              {recent.length ? (
                <div className="space-y-1">{recent.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={recent} />)}</div>
              ) : (
                <p className="text-sm text-slate-600">Play something and it'll show up here.</p>
              )}
            </>
          )}
        </div>
      )}

      {tab === "playlists" && (
        <div data-testid="music-playlists-tab">
          {playlists.length ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
              {playlists.map((p) => <ArtworkCard key={p.id} item={p} testId={`playlist-${p.id}`} />)}
            </div>
          ) : (
            <Empty>No playlists yet.</Empty>
          )}
        </div>
      )}

      {tab === "home" && (
        <section className="mt-2" data-testid="music-all-tracks">
          <h2 className="mb-3 font-heading text-lg font-bold tracking-tight text-white">All tracks</h2>
          {tracks.length === 0 ? (
            <Empty>No music yet.</Empty>
          ) : (
            <div className="space-y-1">{tracks.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={tracks} />)}</div>
          )}
        </section>
      )}
    </div>
  );
};

export default Music;
